<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { SelectIcon, SelectTrigger, type SelectTriggerProps } from 'reka-ui'
import { ChevronDown } from 'lucide-vue-next'
import { cn } from '@/lib/utils'

const props = defineProps<SelectTriggerProps & { class?: HTMLAttributes['class'] }>()

/**
 * reka-ui focuses the trigger on click (a Safari fix), but a mouse opens the select on
 * pointerdown, so that click lands after the list has focused its picked item and pulls
 * focus back out of it: nothing highlighted, arrow keys dead until the pointer hovers an
 * item. Focus coming back from the trigger's own open list is handed straight back.
 */
const keepFocusInOpenList = (event: FocusEvent) => {
    const trigger = event.currentTarget as HTMLElement
    const from = event.relatedTarget

    if (trigger.getAttribute('aria-expanded') !== 'true' || !(from instanceof HTMLElement)) {
        return
    }

    if (from.closest('[role="listbox"]')?.id !== trigger.getAttribute('aria-controls')) {
        return
    }

    from.focus({ preventScroll: true })
}
</script>

<template>
    <SelectTrigger
        v-bind="{ ...props, class: undefined }"
        :class="
            cn(
                'flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:truncate',
                props.class,
            )
        "
        @focus="keepFocusInOpenList"
    >
        <slot />
        <SelectIcon as-child>
            <ChevronDown class="size-4 shrink-0 opacity-50" />
        </SelectIcon>
    </SelectTrigger>
</template>
